'use client';

import { useCallback, type CSSProperties } from 'react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { getPickerIcon } from '@/lib/picker-icons';
import { TicketCardCard } from './TicketCard';
import type { Status } from 'dico-types';
import type { Ticket } from 'dico-types';
import { NO_STATUS_ID } from './TicketListByStatus';
import type { MonoIconType } from '@/components/ui/icon';

export type StatusGroup =
  | Status
  | { id: typeof NO_STATUS_ID; name: string; color?: string; icon?: string };

interface TicketKanbanProps {
  tickets: Ticket[];
  statusGroups: StatusGroup[];
  workspaceId: string;
  onStatusChange: (ticketId: string, newStatusId: string | null) => void;
}

function getStatusGroupId(ticket: Ticket): string {
  return ticket.statusId ?? NO_STATUS_ID;
}

function DroppableColumn({
  id,
  iconType,
  iconStyle,
  title,
  ticketCount,
  children
}: {
  id: string;
  iconType: MonoIconType;
  iconStyle?: CSSProperties;
  title: string;
  ticketCount: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'border-border shadow-xs flex min-h-[200px] w-80 shrink-0 flex-col rounded-md border transition-colors',
        isOver && 'ring-ring/50 ring-2'
      )}
    >
      <div className="flex items-center gap-2 p-3">
        <span className="text-muted-foreground shrink-0">
          <MonoIcon type={iconType} style={iconStyle} />
        </span>
        <h3 className="text-foreground text-sm font-medium">{title}</h3>
        <span className="text-muted-foreground text-sm">{ticketCount}</span>
      </div>
      <div className="flex flex-1 flex-col gap-1 overflow-y-auto p-1 pt-0">{children}</div>
    </div>
  );
}

function DraggableTicketCard({ ticket, workspaceId }: { ticket: Ticket; workspaceId: string }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: ticket.id
  });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('touch-none', isDragging && 'opacity-50')}
      {...listeners}
      {...attributes}
    >
      <div className="min-w-0 flex-1">
        <TicketCardCard ticket={ticket} workspaceId={workspaceId} asDiv />
      </div>
    </div>
  );
}

export function TicketKanban({
  tickets,
  statusGroups,
  workspaceId,
  onStatusChange
}: TicketKanbanProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const ticketId = String(active.id);
      const targetStatusId = String(over.id);
      const ticket = tickets.find((t) => t.id === ticketId);
      if (!ticket) return;
      const currentStatusId = ticket.statusId ?? NO_STATUS_ID;
      if (targetStatusId === currentStatusId) return;
      onStatusChange(ticketId, targetStatusId === NO_STATUS_ID ? null : targetStatusId);
    },
    [tickets, onStatusChange]
  );

  const ticketsByStatus = new Map<string, Ticket[]>();
  for (const g of statusGroups) {
    const id = 'id' in g ? g.id : '';
    ticketsByStatus.set(id, []);
  }
  for (const t of tickets) {
    const id = getStatusGroupId(t);
    if (!ticketsByStatus.has(id)) ticketsByStatus.set(id, []);
    ticketsByStatus.get(id)!.push(t);
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex flex-1 gap-4 overflow-x-auto p-6">
        {statusGroups.map((group) => {
          const id = 'id' in group ? group.id : '';
          const title = 'id' in group && group.id === NO_STATUS_ID ? group.name : group.name;
          const columnTickets = ticketsByStatus.get(id) ?? [];
          const iconType = getPickerIcon(group.icon) ?? 'Circle';
          const iconStyle = group.color ? { color: group.color } : undefined;
          return (
            <DroppableColumn
              key={id}
              id={id}
              title={title}
              iconType={iconType}
              iconStyle={iconStyle}
              ticketCount={columnTickets.length}
            >
              {columnTickets.map((ticket) => (
                <DraggableTicketCard key={ticket.id} ticket={ticket} workspaceId={workspaceId} />
              ))}
            </DroppableColumn>
          );
        })}
      </div>
    </DndContext>
  );
}
